import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { MessageService } from 'primeng/api'
import { Subject, takeUntil } from 'rxjs'

import { CobranzaShellService } from '@modules/private/cobranza/data/services/view/cobranza-shell.service'
import { InvoicesTableComponent } from './components/invoices-table/invoices-table.component'
import { ApiInvoiceService } from './data/services/api/api-invoice.service'
import { CONCEPT_COLOR, CONCEPT_LABEL } from './data/constants/conceptos'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { INVOICE_COLUMNS } from './data/constants/invoice.constants'

interface InvoiceSummary {
  month: string
  facturasDelMes: { count: number; amount: number }
  porConcepto: { concept: string; count: number; amount: number }[]
  pendientes: number
  canceladas: number
  pagadas: number
  pendientesPago: number
  pendientesComplemento: number
}

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
  providers: [MessageService],
})
export class InvoicesComponent implements OnInit, OnDestroy {
  //* Dentro del contenedor de Cobranza el encabezado lo pinta el contenedor. La ruta suelta no lo
  //* pasa y la pantalla queda exactamente como estaba.
  /**
   * El mes lo manda Cobranza — es el MISMO que rige el embudo.
   *
   * Esta vista tenía el suyo propio en su encabezado. Dentro de Cobranza ese encabezado no se pinta,
   * así que el selector quedaba escondido y la vista se quedaba clavada en el mes en curso mientras
   * el embudo, dos centímetros arriba, enseñaba otro. Dos meses en la misma pantalla, uno invisible.
   *
   * Llega por servicio y no por `@Input`: a esta vista la crea el enrutador dentro del cascarón de
   * Cobranza, y un componente enrutado no recibe entradas de su padre.
   */
  private readonly destroyMes$ = new Subject<void>()

  @ViewChild('invoicesTable') invoicesTable?: InvoicesTableComponent

  // Dashboard
  selectedMonth: Date = new Date()
  loadingSummary = false
  summary: InvoiceSummary | null = null

  // Tabla
  columns = INVOICE_COLUMNS
  rowActions = [
    { icon: 'pi pi-file-pdf', color: 'danger', action: 'DOWNLOAD_PDF', title: 'PDF', rule: { field: 'pdfUrl', value: null, type: 'notEquals' } },
    { icon: 'pi pi-file', color: 'info', action: 'DOWNLOAD_XML', title: 'XML', rule: { field: 'xmlUrl', value: null, type: 'notEquals' } },
    // Cargar: solo en referencias PENDING (sin archivos) → completa la factura. En PENDING no aplican Refacturar/Cancelar.
    {
      icon: 'pi pi-cloud-upload',
      color: 'info',
      action: 'CARGAR',
      title: 'Cargar',
      permission: ['invoices', 'create'] as [string, string],
      rule: { field: 'status', value: 'PENDING_FILES', type: 'equals' },
    },
    // Refacturar/Cancelar: solo en facturas con CFDI emitido (LINKED).
    {
      icon: 'pi pi-refresh',
      color: 'warning',
      action: 'REFACTURAR',
      title: 'Refacturar',
      permission: ['invoices', 'update'] as [string, string],
      rule: { field: 'status', value: 'LINKED', type: 'equals' },
    },
    { icon: 'pi pi-ban', color: 'danger', action: 'CANCELAR', title: 'Cancelar', permission: ['invoices', 'update'] as [string, string], rule: { field: 'status', value: 'LINKED', type: 'equals' } },
    // Complementos de pago (REP): solo en facturas PPD (las únicas que llevan complemento).
    { icon: 'pi pi-receipt', color: 'help', action: 'COMPLEMENTOS', title: 'Complementos de pago', rule: { field: 'paymentType', value: 'PPD', type: 'equals' } },
    // Historial: en las filas canceladas/reemplazadas (active=false); el modal muestra la cadena completa.
    { icon: 'pi pi-history', color: 'secondary', action: 'HISTORY', title: 'Historial', rule: { field: 'active', value: false, type: 'equals' } },
  ]
  additionalMainActions = [{ label: 'Carga masiva', icon: 'fa-solid fa-cloud-arrow-up', action: 'UPLOAD' }]

  // Modal carga masiva
  showUpload = false

  // Reporte mensual
  downloadingReport = false

  // Modal complementos de pago (REP)
  showComplements = false
  complementsInvoice: any = null

  // Modal descarga reporte FACTURAS PENDIENTES (Central de Facturas)
  showPendingExport = false
  pendingStartFolio: number | null = null // folio autocalculado (solo informativo)
  pendingFolioSource: 'db' | 'seed' | null = null
  pendingFolioLoading = false
  pendingMonth: Date = new Date()
  pendingExporting = false

  // Diálogo refacturar
  showRefacturar = false
  refacturarInvoiceId: string | null = null
  refacturarCurrentUid = ''
  refacturarNewFolioFiscal = ''
  refacturarReason = ''
  refacturarPdf: File | null = null
  refacturarXml: File | null = null
  refacturando = false

  // Diálogo cancelar
  showCancel = false
  cancelInvoiceId: string | null = null
  cancelUid = ''
  cancelReason = ''
  cancelando = false

  // Diálogo historial
  showHistory = false
  historyTitle = ''
  historyInvoices: any[] = []
  loadingHistory = false
  downloadingHistoryKey: string | null = null

  // Diálogo editar la referencia (una factura PENDING cargada con datos equivocados)
  showEditRef = false
  editRefInvoice: any = null
  editRefFolio = ''
  editRefLease = ''
  editRefConcept = ''
  editRefMonth = ''
  editRefCategory = ''
  editRefReason = ''
  savingRef = false
  readonly editRefConcepts = [
    { value: 'RENT', label: 'Renta (periodo)' },
    { value: 'FIRST_PAYMENT', label: 'Pago inicial' },
    { value: 'RESIDUAL', label: 'Residual' },
    { value: 'MANAGEMENT_EXPENSE', label: 'Gestoría' },
  ]
  readonly editRefCategories = [
    { value: 'TENENCIA', label: 'Tenencia' },
    { value: 'PLACAS', label: 'Placas' },
    { value: 'GESTORIA', label: 'Gestoría' },
    { value: 'OTRO', label: 'Otro' },
  ]

  // Diálogo cargar (completar una factura PENDING)
  showUploadSingle = false
  uploadInvoiceId: string | null = null
  uploadFolioFiscal = ''
  uploadPdf: File | null = null
  uploadXml: File | null = null
  uploadingSingle = false

  constructor(
    public api: ApiInvoiceService,
    private readonly messageService: MessageService,
    private readonly authService: AuthService,
    private readonly shell: CobranzaShellService,
  ) {}

  ngOnInit(): void {
    //* El mes lo publica el cascarón de Cobranza. `BehaviorSubject` emite el actual al suscribirse,
    //* así que esta primera emisión ES la carga inicial: no hace falta pedirla aparte.
    this.shell.month$.pipe(takeUntil(this.destroyMes$)).subscribe((mes) => {
      this.selectedMonth = mes || new Date()
      this.loadSummary()
    })
  }

  ngOnDestroy(): void {
    this.destroyMes$.next()
    this.destroyMes$.complete()
  }

  get monthLabel(): string {
    const d = this.selectedMonth || new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }

  private get userId(): string | undefined {
    return this.authService.getUser?.user?.id
  }

  // ── KPIs / resumen ──
  loadSummary(): void {
    this.loadingSummary = true
    this.api.getSummary(this.monthLabel).subscribe({
      next: (r) => {
        this.loadingSummary = false
        this.summary = r.data
      },
      error: () => {
        this.loadingSummary = false
      },
    })
  }

  get hasConceptData(): boolean {
    return (this.summary?.porConcepto || []).some((c) => c.count > 0)
  }

  conceptLabel(concept: string): string {
    return CONCEPT_LABEL[concept] || concept
  }
  conceptColor(concept: string): string {
    return CONCEPT_COLOR[concept] || '#cbd5e1'
  }

  /**
   * El desglose por concepto, listo para pintar: **una barra apilada, no una dona**.
   *
   * La dona ocupaba 220 px de alto más su leyenda más una lista al lado —unos 330 px— para decir
   * tres cifras. Y encima medía por CONTEO de facturas, no por importe, así que una gestoría de
   * $8,500 se veía igual de grande que un enganche de $1.3M: el dibujo contradecía a la lista que
   * tenía al lado.
   *
   * La barra apilada mide por IMPORTE, que es lo que se está desglosando, y ordena de mayor a menor:
   * la pregunta que contesta es «de lo facturado del mes, ¿qué pesa?».
   */
  get conceptos(): { concept: string; label: string; color: string; count: number; amount: number; pct: number }[] {
    const segs = (this.summary?.porConcepto || []).filter((c) => c.count > 0)
    const total = segs.reduce((s, c) => s + (c.amount || 0), 0)

    return segs
      .map((c) => ({
        concept: c.concept,
        label: CONCEPT_LABEL[c.concept] || c.concept,
        color: CONCEPT_COLOR[c.concept] || '#cbd5e1',
        count: c.count,
        amount: c.amount || 0,
        //* Un concepto con importe cero pero con facturas existe —una nota de $0— y sin el mínimo
        //* desaparecería de la barra sin desaparecer de la lista.
        pct: total > 0 ? Math.max(0.6, ((c.amount || 0) / total) * 100) : 100 / segs.length,
      }))
      .sort((a, b) => b.amount - a.amount)
  }

  // ── Tabla ──
  onMainAction(action: string): void {
    if (action === 'UPLOAD') this.showUpload = true
  }

  onRowAction(e: { action: string; data: any }): void {
    const inv = e?.data
    switch (e?.action) {
      case 'DOWNLOAD_PDF':
        this.downloadFile(inv, 'pdf')
        break
      case 'DOWNLOAD_XML':
        this.downloadFile(inv, 'xml')
        break
      case 'REFACTURAR':
        this.openRefacturar(inv)
        break
      case 'CANCELAR':
        this.openCancel(inv)
        break
      case 'HISTORY':
        this.openHistory(inv)
        break
      case 'CARGAR':
        this.openUploadSingle(inv)
        break
      case 'EDITAR':
        this.openEditRef(inv)
        break
      case 'COMPLEMENTOS':
        this.openComplements(inv)
        break
    }
  }

  // ── Complementos de pago (REP) ──
  openComplements(inv: any): void {
    this.complementsInvoice = inv
    this.showComplements = true
  }

  onComplementsChanged(): void {
    // Al ligar un REP la factura puede pasar a "Recibido" → refrescar tabla y KPIs.
    this.refresh()
  }

  // ── Reporte mensual descargable ──
  downloadMonthlyReport(): void {
    this.downloadingReport = true
    this.api.downloadMonthlyReport(this.monthLabel).subscribe({
      next: (res) => {
        this.downloadingReport = false
        const blob = res.body as Blob | null
        if (!blob || blob.size === 0) {
          this.toastErr('Reporte mensual', 'No se pudo generar el reporte.')
          return
        }
        const filename = this.filenameFromResponse(res) || `REPORTE FACTURAS ${this.monthLabel}.xlsx`
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
        this.toastOk('Reporte generado', `Se descargó ${filename}.`)
      },
      error: () => {
        this.downloadingReport = false
        this.toastErr('Reporte mensual', 'No se pudo generar el reporte.')
      },
    })
  }

  onUploadCompleted(): void {
    this.refresh()
  }

  /**
   * Se recarga lo propio Y se avisa al embudo del mes.
   *
   * El embudo vive en el cascarón de Cobranza, así que desde que dejó de recrearse al cambiar de
   * vista ya no se entera solo. Sin el aviso, cargar cuatro facturas dejaba «Facturado $0.00» arriba
   * mientras la tabla de abajo las enseñaba: una cifra falsa es peor que ninguna.
   */
  private refresh(): void {
    this.loadSummary()
    this.invoicesTable?.recargar()
    this.shell.refresh()
  }

  downloadFile(inv: any, kind: 'pdf' | 'xml'): void {
    if (!inv?._id) return
    this.api.downloadFile(inv._id, kind).subscribe({
      next: (res) => {
        const blob = res.body as Blob | null
        if (!blob) return
        const filename = this.filenameFromResponse(res) || `${inv.folioFiscal || inv.uid}.${kind}`
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      },
      error: () => this.toastErr('Factura', `No se pudo descargar el ${kind.toUpperCase()}.`),
    })
  }

  // ── Central de Facturas (reporte FACTURAS PENDIENTES) ──
  openPendingExport(): void {
    this.pendingStartFolio = null
    this.pendingFolioSource = null
    this.pendingMonth = this.selectedMonth || new Date()
    this.showPendingExport = true
    // El folio inicial ya no lo teclea el usuario: se autocalcula en el backend. Aquí solo lo
    // mostramos como referencia (la descarga vuelve a recalcularlo para evitar carreras).
    this.pendingFolioLoading = true
    this.api.getNextFolio().subscribe({
      next: (r) => {
        this.pendingFolioLoading = false
        this.pendingStartFolio = r.data?.nextFolio ?? null
        this.pendingFolioSource = r.data?.source ?? null
      },
      error: () => {
        this.pendingFolioLoading = false
      },
    })
  }

  confirmPendingExport(): void {
    const d = this.pendingMonth || new Date()
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    this.pendingExporting = true
    // Sin startFolio: el backend lo recalcula al momento de generar (idempotente y libre de carreras).
    this.api.downloadPendingInvoices(undefined, month).subscribe({
      next: (res) => {
        this.pendingExporting = false
        const blob = res.body as Blob | null
        if (!blob || blob.size === 0) {
          this.toastErr('Central de Facturas', 'No hay arrendamientos con facturación configurada para exportar.')
          return
        }
        const filename = this.filenameFromResponse(res) || 'FACTURAS PENDIENTES.xlsx'
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
        this.showPendingExport = false
        this.toastOk('Reporte generado', 'Se descargó FACTURAS PENDIENTES.xlsx.')
      },
      error: () => {
        this.pendingExporting = false
        this.toastErr('Central de Facturas', 'No se pudo generar el reporte.')
      },
    })
  }

  private filenameFromResponse(res: any): string | null {
    const cd = res?.headers?.get?.('content-disposition') || res?.headers?.get?.('Content-Disposition')
    if (!cd) return null
    const star = /filename\*=UTF-8''([^;]+)/i.exec(cd)
    if (star) return decodeURIComponent(star[1])
    const m = /filename="?([^";]+)"?/i.exec(cd)
    return m ? m[1] : null
  }

  // ── Refacturar ──
  openRefacturar(inv: any): void {
    if (!inv?._id) return
    this.refacturarInvoiceId = inv._id
    this.refacturarCurrentUid = inv.uid
    this.refacturarNewFolioFiscal = ''
    this.refacturarReason = ''
    this.refacturarPdf = null
    this.refacturarXml = null
    this.showRefacturar = true
  }
  onRefacturarPdf(e: Event): void {
    this.refacturarPdf = (e.target as HTMLInputElement).files?.[0] || null
  }
  onRefacturarXml(e: Event): void {
    this.refacturarXml = (e.target as HTMLInputElement).files?.[0] || null
  }
  confirmRefacturar(): void {
    if (!this.refacturarInvoiceId) return
    const files: File[] = []
    if (this.refacturarPdf) files.push(this.refacturarPdf)
    if (this.refacturarXml) files.push(this.refacturarXml)
    // El Folio Fiscal se deriva de los archivos si se adjuntan; si no, hay que teclearlo.
    const folioFiscal = this.refacturarNewFolioFiscal.trim()
    if (!folioFiscal && !files.length) return
    this.refacturando = true
    this.api
      .refacturar(this.refacturarInvoiceId, { newFolioFiscal: folioFiscal || undefined, reason: this.refacturarReason.trim() || undefined, userId: this.userId }, files.length ? files : undefined)
      .subscribe({
        next: (r) => {
          this.refacturando = false
          if (r.error) {
            this.toastErr('Refacturar', r.msg)
            return
          }
          this.showRefacturar = false
          this.toastOk('Factura refacturada', 'Se creó la nueva factura y se conservó el historial.')
          this.refresh()
        },
        error: () => {
          this.refacturando = false
          this.toastErr('Refacturar', 'No se pudo refacturar la factura.')
        },
      })
  }

  // ── Cancelar ──
  openCancel(inv: any): void {
    if (!inv?._id) return
    this.cancelInvoiceId = inv._id
    this.cancelUid = inv.uid
    this.cancelReason = ''
    this.showCancel = true
  }
  confirmCancel(): void {
    if (!this.cancelInvoiceId || !this.cancelReason.trim()) return
    this.cancelando = true
    this.api.cancel(this.cancelInvoiceId, { reason: this.cancelReason.trim(), userId: this.userId }).subscribe({
      next: (r) => {
        this.cancelando = false
        if (r.error) {
          this.toastErr('Cancelar', r.msg)
          return
        }
        this.showCancel = false
        this.toastOk('Factura cancelada', 'La factura quedó cancelada (historial conservado).')
        this.refresh()
      },
      error: () => {
        this.cancelando = false
        this.toastErr('Cancelar', 'No se pudo cancelar la factura.')
      },
    })
  }

  // ── Historial ──
  openHistory(inv: any): void {
    if (!inv?._id) return
    this.historyTitle = inv.leaseFolio ? `Historial · ${inv.leaseFolio}${inv.month ? ' · ' + inv.month : ''}` : 'Historial de la factura'
    this.historyInvoices = []
    this.loadingHistory = true
    this.showHistory = true
    this.api.getInvoiceHistory(inv._id).subscribe({
      next: (r) => {
        this.loadingHistory = false
        this.historyInvoices = r.data || []
      },
      error: () => {
        this.loadingHistory = false
      },
    })
  }

  // Folio Fiscal de una factura ligada (replaces/replacedBy) resuelto dentro de la cadena cargada.
  folioFiscalOf(ref: any): string {
    if (!ref) return ''
    const id = String(ref?._id || ref)
    return this.historyInvoices.find((i) => String(i._id) === id)?.folioFiscal || ''
  }

  historyStatusLabel(status: string): string {
    const m: Record<string, string> = { PENDING_FILES: 'Pendiente', LINKED: 'Vigente', REPLACED: 'Reemplazada', CANCELLED: 'Cancelada' }
    return m[status] || status || '—'
  }
  historyStatusSeverity(status: string): string {
    const m: Record<string, string> = { PENDING_FILES: 'warning', LINKED: 'success', REPLACED: 'info', CANCELLED: 'danger' }
    return m[status] || 'info'
  }
  isDownloadingHistory(inv: any, kind: 'pdf' | 'xml'): boolean {
    return !!inv && this.downloadingHistoryKey === `${inv._id}-${kind}`
  }
  downloadHistoryFile(inv: any, kind: 'pdf' | 'xml'): void {
    if (!inv?._id) return
    this.downloadingHistoryKey = `${inv._id}-${kind}`
    this.api.downloadFile(inv._id, kind).subscribe({
      next: (res) => {
        this.downloadingHistoryKey = null
        const blob = res.body as Blob | null
        if (!blob) {
          this.toastErr('Factura', 'El servidor no devolvió contenido.')
          return
        }
        const filename = this.filenameFromResponse(res) || `${inv.folioFiscal || inv.uid || inv._id}.${kind}`
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      },
      error: () => {
        this.downloadingHistoryKey = null
        this.toastErr('Factura', `No se pudo descargar el ${kind.toUpperCase()}.`)
      },
    })
  }

  // ── Editar la referencia de una factura PENDING ──
  //! La carga de archivos encuentra la factura SOLO por su folio fiscal: si el Excel de carga lo
  //! trajo mal, el PDF/XML se omitía y no había cómo arreglarlo. Esto es cómo.
  openEditRef(inv: any): void {
    if (!inv?._id) return
    this.editRefInvoice = inv
    this.editRefFolio = inv.folioFiscal || ''
    this.editRefLease = inv.leaseFolio || ''
    this.editRefConcept = inv.concept || 'RENT'
    this.editRefMonth = inv.month || ''
    this.editRefCategory = inv.expenseCategory || ''
    this.editRefReason = ''
    this.showUploadSingle = false
    this.showEditRef = true
  }

  get canEditRef(): boolean {
    return this.authService.hasModulePermission('invoices', 'update')
  }

  /** Desde «Cargar»: el archivo trae otro folio y hay que corregir la referencia antes. */
  editRefFromUpload(): void {
    const inv = this.invoicesTable?.filas?.find((f) => f._id === this.uploadInvoiceId)?.raw
    if (inv) this.openEditRef(inv)
  }

  confirmEditRef(): void {
    const inv = this.editRefInvoice
    if (!inv?._id) return
    //* Se manda solo lo que cambió: la bitácora registra un renglón por campo tocado.
    const patch: any = { reason: this.editRefReason.trim() || undefined }
    if (this.editRefFolio.trim() !== (inv.folioFiscal || '')) patch.folioFiscal = this.editRefFolio.trim()
    if (this.editRefLease.trim() !== (inv.leaseFolio || '')) patch.lease = this.editRefLease.trim()
    if (this.editRefConcept !== inv.concept) patch.concept = this.editRefConcept
    if (this.editRefMonth.trim() !== (inv.month || '')) patch.month = this.editRefMonth.trim() || null
    if (this.editRefConcept === 'MANAGEMENT_EXPENSE' && this.editRefCategory !== (inv.expenseCategory || '')) patch.expenseCategory = this.editRefCategory || null
    if (Object.keys(patch).length === 1) {
      this.showEditRef = false
      return
    }
    this.savingRef = true
    this.api.updateReference(inv._id, patch).subscribe((r) => {
      this.savingRef = false
      if (r.error) {
        this.toastErr('Editar referencia', r.msg)
        return
      }
      this.showEditRef = false
      this.toastOk('Referencia corregida', `Ya puedes subir el PDF y el XML de ${r.data?.folioFiscal || 'la factura'}. El cambio quedó en la bitácora.`)
      this.refresh()
    })
  }

  // ── Cargar archivos de una factura PENDING (completarla) ──
  openUploadSingle(inv: any): void {
    if (!inv?._id) return
    this.uploadInvoiceId = inv._id
    this.uploadFolioFiscal = inv.folioFiscal || ''
    this.uploadPdf = null
    this.uploadXml = null
    this.showUploadSingle = true
  }
  onUploadPdf(e: Event): void {
    this.uploadPdf = (e.target as HTMLInputElement).files?.[0] || null
  }
  onUploadXml(e: Event): void {
    this.uploadXml = (e.target as HTMLInputElement).files?.[0] || null
  }
  confirmUploadSingle(): void {
    if (!this.uploadInvoiceId) return
    const files: File[] = []
    if (this.uploadPdf) files.push(this.uploadPdf)
    if (this.uploadXml) files.push(this.uploadXml)
    if (!files.length) {
      this.toastErr('Cargar', 'Selecciona al menos el PDF o el XML.')
      return
    }
    this.uploadingSingle = true
    this.api.uploadForInvoice(this.uploadInvoiceId, files, this.userId).subscribe({
      next: (r) => {
        this.uploadingSingle = false
        if (r.error) {
          this.toastErr('Cargar', r.msg)
          return
        }
        this.showUploadSingle = false
        this.toastOk('Factura cargada', r.data?.warning ? `Completada · ⚠ ${r.data.warning}` : 'La factura quedó ligada.')
        this.refresh()
      },
      error: () => {
        this.uploadingSingle = false
        this.toastErr('Cargar', 'No se pudo cargar la factura.')
      },
    })
  }

  private toastOk(summary: string, detail: string): void {
    this.messageService.add({ severity: 'success', summary, detail, life: 6000 })
  }
  private toastErr(summary: string, detail: string): void {
    this.messageService.add({ severity: 'error', summary, detail, life: 8000 })
  }
}
